import type { ContentBlock, DocumentContent, LegalDocument, RichText, TextRun } from './interfaces';

// Turns the string held in Firestore back into a LegalDocument.
//
// The field is an ARBITRARY string that a human edits in the Firebase console, so this parser
// validates rather than trusts: JSON.parse throws on malformed input, and — the case that
// actually bites — input that parses cleanly can still be the wrong shape. An unchecked cast
// there renders a half-empty legal document, which is worse than showing nothing at all.
//
// Returns null on any failure. The page treats null exactly like a failed fetch: the error
// state, never a partial document. Hand-written type guards rather than a schema library —
// the absence of dependencies is the design (CLAUDE.md §2).

const isString = (value: unknown): value is string => typeof value === 'string';

// An object, and specifically not an array or null — `typeof null === 'object'` is the classic
// way a guard like this lets bad input through.
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isTextRun = (value: unknown): value is TextRun =>
  isRecord(value) &&
  isString(value.text) &&
  (value.strong === undefined || typeof value.strong === 'boolean') &&
  (value.href === undefined || isString(value.href));

const isRichText = (value: unknown): value is RichText =>
  isString(value) || (Array.isArray(value) && value.every(isTextRun));

const isContentBlock = (value: unknown): value is ContentBlock => {

  if (isString(value)) { return true; }
  if (!isRecord(value)) { return false; }

  switch (value.type) {
    case 'paragraph': return isRichText(value.text);
    case 'subheading': return isString(value.text);
    case 'list': return Array.isArray(value.items) && value.items.every(isRichText);
    default: return false;
  }
};

const isDocumentContent = (value: unknown): value is DocumentContent =>
  isRecord(value) &&
  isString(value.heading) &&
  Array.isArray(value.body) &&
  value.body.every(isContentBlock);

const isLegalDocument = (value: unknown): value is LegalDocument =>
  isRecord(value) &&
  isString(value.id) &&
  isString(value.title) &&
  isString(value.version) &&
  isString(value.effectiveDate) &&
  isString(value.intro) &&
  Array.isArray(value.sections) &&
  value.sections.every(isDocumentContent);

export const parseLegalDocument = (raw: unknown): LegalDocument | null => {

  if (!isString(raw)) { return null; }

  let parsed: unknown;

  try { parsed = JSON.parse(raw); }
  catch { return null; }

  return isLegalDocument(parsed) ? parsed : null;
};

// Normalizers, so the renderer handles ONE shape instead of branching on the shorthands.
// Keeping this here means the shorthand rules are defined and applied in the same file.

export const toBlock = (block: ContentBlock): Exclude<ContentBlock, string> =>
  isString(block) ? { type: 'paragraph', text: block } : block;

export const toRuns = (text: RichText): TextRun[] =>
  isString(text) ? [{ text }] : text;
